import { useEffect, useState, type ReactNode } from "react";
import { telemetry } from "./telemetry";

interface ManifestEntry {
  id: string;
  url: string;
  kind: "texture" | "model" | "env";
}

/**
 * Manifiesto de activos. Todo se sirve desde /public o es procedural;
 * no hay descargas externas en runtime.
 */
const ASSET_MANIFEST: readonly ManifestEntry[] = [
  { id: "placeholder-grid", url: "/assets/grid.txt", kind: "texture" },
];

export function useAssetManifestProgress(): { loaded: number; total: number; ready: boolean } {
  const [loaded, setLoaded] = useState(0);
  const total = ASSET_MANIFEST.length;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const entry of ASSET_MANIFEST) {
        try {
          await fetch(entry.url, { method: "HEAD" }).catch(() => undefined);
        } finally {
          if (!cancelled) {
            setLoaded((n) => n + 1);
            telemetry.info("assets", `cargado: ${entry.id}`);
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { loaded, total, ready: loaded >= total };
}

export function AssetManifestLoader({ children }: { children: ReactNode }) {
  const { loaded, total, ready } = useAssetManifestProgress();

  if (!ready) {
    const pct = total === 0 ? 100 : Math.round((loaded / total) * 100);
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex h-full w-full flex-col items-center justify-center gap-3 bg-[var(--cale-surface)]"
      >
        <p className="text-sm text-[var(--cale-ink-muted)]">Cargando recursos de la simulación…</p>
        <div
          className="h-2 w-64 overflow-hidden rounded-full bg-[var(--cale-border)]"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Progreso de carga de recursos"
        >
          <div className="h-full bg-[var(--cale-accent)] transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-[var(--cale-ink-muted)]">{pct}%</p>
      </div>
    );
  }

  return <>{children}</>;
}
